/**
 * ReviewAggregateChipsPage — Reference · Review Aggregate Chips.
 *
 * Config for the aggregate chip row rendered at the top of the Review screen
 * (Screen 26 SPEC) — e.g. "Accounts to create", "Total deposit". Each chip is
 * keyed; displayOrder sorts the row; visible toggles it.
 *
 * Contract (verified 2026-06-16 — AdminComplianceService upsert persists every
 * field; single natural key ChipKey):
 *   - Read:   GET    /v3/admin/review-aggregate-chip
 *   - Write:  POST   /v3/admin/review-aggregate-chip
 *   - Delete: DELETE /v3/admin/review-aggregate-chip/{chipKey}
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, NumberField, TextArea, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface Row {
  chipKey: string;
  displayLabel: string;
  displayOrder: number;
  visible: boolean;
  description: string | null;
}

interface EditDraft extends Row {
  isNew: boolean;
}

function emptyDraft(): EditDraft {
  return { chipKey: '', displayLabel: '', displayOrder: 100, visible: true, description: '', isNew: true };
}

export function ReviewAggregateChipsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const r = await client.http.get<{ success: boolean; data?: ReadonlyArray<Row>; error?: string }>(
          '/v3/admin/review-aggregate-chip',
        );
        if (cancelled) return;
        if (r.success && Array.isArray(r.data)) setRows(r.data);
        else setLoadError(r.error ?? 'Failed to load chips.');
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.chipKey.trim() || !draft.displayLabel.trim()) {
      setSaveError('Chip key and display label are both required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const r = await client.http.post<{ success: boolean; error?: string }>('/v3/admin/review-aggregate-chip', {
        headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
        body: { ...draft, description: draft.description?.trim() || null },
      });
      if (!r.success) throw new Error(r.error ?? 'save failed');
      setDraft(null);
      setToast({ tone: 'info', text: `Saved ${draft.chipKey}.` });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      const r = await client.http.del<{ success: boolean; error?: string }>(
        `/v3/admin/review-aggregate-chip/${encodeURIComponent(deleteTarget.chipKey)}`,
        { headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL) },
      );
      if (!r.success) throw new Error(r.error ?? 'delete failed');
      setToast({ tone: 'warn', text: `Deleted ${deleteTarget.chipKey}.` });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, client, refresh]);

  const columns: ReadonlyArray<DataTableColumn<Row>> = [
    { header: 'Key', cell: (r) => <code className="cell-mono--bold">{r.chipKey}</code> },
    { header: 'Label', cell: (r) => <span className="cell-strong">{r.displayLabel}</span> },
    { header: 'Order', cell: (r) => <span className="num">{r.displayOrder}</span> },
    { header: 'Visible', cell: (r) => (r.visible ? <Pill tone="ok">visible</Pill> : <Pill tone="muted">hidden</Pill>) },
    {
      header: '',
      cell: (r) => (
        <div className="card-actions-row">
          <Button onClick={() => { setDraft({ ...r, description: r.description ?? '', isNew: false }); setSaveError(null); }}>Edit</Button>
          <Button onClick={() => setDeleteTarget(r)}>Delete</Button>
        </div>
      ),
    },
  ];

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load chips: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading review aggregate chips…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Review Aggregate Chips — ${rows.length}`}
        actions={
          <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
            + New chip
          </Button>
        }
      >
        <p className="cell-desc">The aggregate chip row at the top of the Review screen (Screen 26). Sorted by display order.</p>
        <DataTable<Row>
          columns={columns}
          rows={rows.slice().sort((a, b) => a.displayOrder - b.displayOrder)}
          rowKey={(r) => r.chipKey}
          emptyState={<p className="empty">No chips defined yet.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'New chip' : `Edit ${draft?.chipKey ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <div className="form-row">
              <TextField
                label="Chip key"
                value={draft.chipKey}
                onChange={(v) => draft.isNew && setDraft({ ...draft, chipKey: v })}
                disabled={!draft.isNew}
                monospace
                placeholder="AccountsToCreate"
              />
              <NumberField
                label="Display order"
                value={draft.displayOrder}
                onChange={(v) => setDraft({ ...draft, displayOrder: v ?? 100 })}
                min={0}
              />
            </div>
            <TextField
              label="Display label"
              value={draft.displayLabel}
              onChange={(v) => setDraft({ ...draft, displayLabel: v })}
              placeholder="Accounts to create"
            />
            <TextArea
              label="Description (hover tooltip)"
              value={draft.description ?? ''}
              onChange={(v) => setDraft({ ...draft, description: v })}
              rows={2}
            />
            <BoolField label="Visible" value={draft.visible} onChange={(v) => setDraft({ ...draft, visible: v })} />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Delete this chip?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={480}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Delete'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              Delete chip <code className="cell-mono">{deleteTarget.chipKey}</code>? It will stop
              rendering on the Review screen.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
